import { useQueryCache } from '@pinia/colada';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
  createManualAllocations,
  createPoAssignments,
  deleteCapacityAllocation,
  deletePoAssignment,
  distributePurchaseOrder as requestDistribution,
  fetchCapacityCalendar,
  moveAssignment as requestMoveAssignment,
  moveDistributionGroup as requestMoveDistributionGroup,
  updateAssignmentNotes,
  updateCapacityAllocation,
  updatePoAssignment,
} from '@/services/capacityCalendarService';
import type { DistributionPortion, DistributionResult, PoAssignmentInput } from '@/services/capacityCalendarService';
import { confirmCapacityImport, parseCapacityPlan } from '@/services/capacityImportService';
import logger from '@/services/logger';
import { TO_SCHEDULE_SEGMENT } from '@/store/poQueueStore';
import { PURCHASE_ORDERS_QUERY_KEY } from '@/store/purchaseOrderStore';
import type { AllocationParametersUpdate, CapacityAllocationMap, CalendarEntry } from '@/types/capacityCalendar';
import type { CapacityImportConfirmResult, CapacityPlanParseResult } from '@/types/capacityImport';
import { extractErrorMessage } from '@/utils/apiError';
import {
  assignmentSuccessMessage,
  distributionSuccessMessage,
  moveSuccessMessage,
  noteUpdateMessage,
  quantityUpdateMessage,
} from '@/utils/assignmentMessages';
import { groupCalendarRows } from '@/utils/capacityCalendar';
import { applyExclusions, buildApprovedProductionLines, buildImportDiff, buildOverwriteTargets } from '@/utils/importPreview';
import type { ImportDiff } from '@/utils/importPreview';

// How long a just-assigned/distributed PO stays highlighted on the calendar.
export const RECENT_ASSIGNMENT_WINDOW_MS = 3000;

export interface RecentAssignments {
  poNumbers: string[];
  // Every week the write touched — a multi-week split flashes all of them at once.
  weekKeys: string[];
  until: number;
}

export interface RecentCells {
  keys: Set<string>;
  until: number;
}

export interface PendingImport {
  fileName: string;
  parse: CapacityPlanParseResult;
}

export interface DistributionGroupMember {
  typeCode: string;
  typeLabel: string;
  capacityTypeId: string;
  quantity: number;
}

export const useCapacityCalendarStore = defineStore('capacityCalendar', () => {
  const entries = ref<CalendarEntry[]>([]);
  const allocations = ref<CapacityAllocationMap>({});
  const lockedVendorIds = ref<string[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const recentAssignments = ref<RecentAssignments | null>(null);
  const recentCells = ref<RecentCells | null>(null);
  const pendingImport = ref<PendingImport | null>(null);
  const importParsing = ref(false);
  // True for the duration of a confirm request — the view uses it to disable Discard, the ×
  // exclude buttons, and the toolbar Import button so nothing can mutate/replace pendingImport
  // while its captured snapshot is in flight (see approvePendingImport's race guard).
  const approving = ref(false);
  // Cells the user removed from the current preview via the per-cell × button, keyed
  // `${rowId}:${weekStart}` — reset whenever a new preview replaces this one. Stale keys (no
  // longer matching any cell in rawPendingDiff, e.g. the file's week drifted to "matched" or
  // its allocation was deleted) are left in the set rather than pruned — pendingDiff's
  // applyExclusions call simply finds nothing to filter for them, so they're naturally inert;
  // excludedCount below is what keeps the bar's "N excluded" chip honest despite that.
  const excludedPendingKeys = ref<Set<string>>(new Set());

  // Reactive, not a stored snapshot: recomputes on ANY change to entries/allocations —
  // including in-place mutations from deleteAllocation/updateAllocationParameters/assignment
  // handlers that never called refresh() — so the preview's classification of a cell (new /
  // changed / matched / needs attention) can never go stale while a preview is active. This is
  // the *pre-exclusion* diff; use pendingDiff (below) for what the grid/bar should render.
  const rawPendingDiff = computed(() =>
    pendingImport.value ? buildImportDiff(pendingImport.value.parse, entries.value, allocations.value) : null,
  );

  // What the grid/bar actually render: rawPendingDiff with the user's × exclusions applied.
  const pendingDiff = computed<ImportDiff | null>(() =>
    rawPendingDiff.value ? applyExclusions(rawPendingDiff.value, excludedPendingKeys.value) : null,
  );

  // Counts only the excluded keys that still match a cell in the current (pre-exclusion) diff —
  // a key whose week drifted out of `cells` (matched now, or its allocation/row disappeared)
  // stops counting toward "N excluded" instead of lingering as a stale, dishonest count.
  const excludedCount = computed(() => {
    const raw = rawPendingDiff.value;
    if (!raw) return 0;
    return [...excludedPendingKeys.value].filter((key) => raw.cells.some((cell) => `${cell.rowId}:${cell.weekStart}` === key)).length;
  });

  const rows = computed(() => groupCalendarRows(entries.value));

  // Members of each multi-type distribution, in calendar row order — feeds the
  // PO chip tooltip's group breakdown.
  const distributionGroups = computed(() => {
    const groups = new Map<string, DistributionGroupMember[]>();
    entries.value.forEach((entry) => {
      Object.values(allocations.value[entry.id] ?? {}).forEach((allocation) => {
        allocation.purchaseOrders.forEach((po) => {
          if (!po.distributionGroupId) return;
          const members = groups.get(po.distributionGroupId) ?? [];
          members.push({
            typeCode: entry.typeCode,
            typeLabel: entry.typeLabel,
            capacityTypeId: entry.capacityTypeId ?? '',
            quantity: po.quantity,
          });
          groups.set(po.distributionGroupId, members);
        });
      });
    });
    return groups;
  });

  const queryCache = useQueryCache();

  // Every assignment write reshapes what the cached Odoo-backed PO read reports
  // (remaining quantities, per-week placements), so its 10-minute cache must not
  // outlive the write. Stale-mark only — the next load() refetches.
  //
  // The to-be-scheduled queue lives under the same prefix but needs its own actively-refetched
  // invalidation: it's read from a store, not a component, so it never remounts to pick up a
  // stale mark on its own — refetchActive: false would leave it showing pre-write data until
  // something else happens to touch it.
  function invalidatePurchaseOrders(): void {
    void queryCache.invalidateQueries({ key: [PURCHASE_ORDERS_QUERY_KEY] }, false);
    void queryCache.invalidateQueries({ key: [PURCHASE_ORDERS_QUERY_KEY, TO_SCHEDULE_SEGMENT] }, true);
  }

  function setRecentAssignments(poNumbers: string[], weekKeys: string[]): void {
    recentAssignments.value = { poNumbers, weekKeys, until: Date.now() + RECENT_ASSIGNMENT_WINDOW_MS };
  }

  // Same highlight pattern as recentAssignments, generalized to arbitrary `${rowId}:${weekKey}`
  // cell keys — used to flash the cells an import just wrote, not just PO assignments.
  function markRecentCells(keys: string[]): void {
    recentCells.value = { keys: new Set(keys), until: Date.now() + RECENT_ASSIGNMENT_WINDOW_MS };
  }

  // Distribution results are keyed by vendorId + capacityTypeId; resolve
  // them back to the calendar row that owns that pair.
  function findRowId(vendorId: string | undefined, capacityTypeId: string): string | undefined {
    if (!vendorId) return undefined;
    return rows.value.find((row) => row.vendorId === vendorId && row.capacityTypeId === capacityTypeId)?.id;
  }

  function patchAllocationFromResults(vendorId: string | undefined, results: DistributionResult[]): void {
    results.forEach(({ capacityTypeId, allocation }) => {
      const targetRowId = findRowId(vendorId, capacityTypeId);
      if (!targetRowId) return;
      if (!allocations.value[targetRowId]) {
        allocations.value[targetRowId] = {};
      }
      allocations.value[targetRowId][allocation.weekKey] = allocation;
    });
  }

  async function load(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const data = await fetchCapacityCalendar();
      entries.value = data.entries;
      allocations.value = data.allocations;
      lockedVendorIds.value = data.lockedVendorIds;
    } catch (loadError) {
      error.value = extractErrorMessage(loadError, 'Failed to load capacity calendar');
    } finally {
      loading.value = false;
    }
  }

  // Silent re-fetch after a mutation: swaps data in place WITHOUT the loading flag, so the
  // grid never unmounts — no progress-bar flash, scroll position survives. Keeps whatever is
  // on screen if the refresh itself fails (the mutation already succeeded).
  //
  // No preview-diff recompute needed here anymore — pendingDiff is a computed derived straight
  // from entries/allocations, so swapping them in below (or any other in-place mutation
  // elsewhere in this store) keeps the preview's classification current on its own.
  async function refresh(): Promise<void> {
    try {
      const data = await fetchCapacityCalendar();
      entries.value = data.entries;
      allocations.value = data.allocations;
      lockedVendorIds.value = data.lockedVendorIds;
    } catch (refreshError) {
      logger.showError(extractErrorMessage(refreshError, 'Failed to refresh the capacity calendar'));
    }
  }

  function getAllocation(rowId: string, weekKey: string) {
    return allocations.value[rowId]?.[weekKey] ?? null;
  }

  async function updateAllocationParameters(rowId: string, weekKey: string, update: AllocationParametersUpdate): Promise<boolean> {
    const allocation = getAllocation(rowId, weekKey);
    if (!allocation) {
      return false;
    }

    try {
      await updateCapacityAllocation(allocation.id, update);
    } catch (updateError) {
      logger.showError(extractErrorMessage(updateError, 'Failed to update allocation'));
      return false;
    }

    const assigned = allocation.purchaseOrders.reduce((total, po) => total + po.quantity, 0);
    Object.assign(allocation, {
      planningCapacity: update.planningCapacity,
      capacityFactor: update.capacityFactor,
      remainedCapacity: update.planningCapacity - assigned,
    });
    return true;
  }

  async function createAllocation(rowId: string, weekKey: string, input: { capacity: number; capacityFactor: number | null }): Promise<boolean> {
    const entry = entries.value.find((candidate) => candidate.id === rowId);
    if (!entry?.vendorId || !entry.capacityTypeId) {
      logger.showError('This row is not linked to a portal vendor and type');
      return false;
    }

    let result;
    try {
      // The server snaps the date to the Monday of its week, so day-cell clicks work too.
      result = await createManualAllocations({
        vendorId: entry.vendorId,
        capacityTypeId: entry.capacityTypeId,
        capacityFactor: input.capacityFactor,
        overwriteExisting: false,
        weeks: [{ startDate: weekKey, capacity: input.capacity }],
      });
    } catch (createError) {
      logger.showError(extractErrorMessage(createError, 'Failed to create allocation'));
      return false;
    }

    // Refresh either way — on a skip the calendar was stale and must show what's really
    // there. Silent refresh: the grid stays mounted, no flash, scroll preserved.
    await refresh();
    if (result.skipped > 0) {
      logger.showError(
        'That week is already planned (the calendar was out of date and has been refreshed) — edit the existing allocation instead.',
      );
      return false;
    }
    return true;
  }

  async function deleteAllocation(rowId: string, weekKey: string): Promise<boolean> {
    const allocation = getAllocation(rowId, weekKey);
    if (!allocation) {
      return false;
    }

    try {
      await deleteCapacityAllocation(allocation.id);
    } catch (deleteError) {
      logger.showError(extractErrorMessage(deleteError, 'Failed to delete allocation'));
      return false;
    }

    const byWeek = allocations.value[rowId];
    if (byWeek) delete byWeek[weekKey];
    invalidatePurchaseOrders();
    return true;
  }

  async function assignPurchaseOrders(rowId: string, weekKey: string, assignments: PoAssignmentInput[]): Promise<boolean> {
    const allocation = getAllocation(rowId, weekKey);
    if (!allocation) {
      return false;
    }

    try {
      const updated = await createPoAssignments(allocation.id, assignments);
      allocations.value[rowId][weekKey] = updated;
      const highlightedPoNumbers = assignments.map((assignment) => assignment.poNumber).filter((poNumber): poNumber is string => Boolean(poNumber));
      if (highlightedPoNumbers.length > 0) {
        setRecentAssignments(highlightedPoNumbers, [weekKey]);
      }
      logger.showSuccess(assignmentSuccessMessage(assignments));
      invalidatePurchaseOrders();
      return true;
    } catch (assignError) {
      logger.showError(extractErrorMessage(assignError, 'Failed to assign purchase orders'));
      return false;
    }
  }

  async function updatePoAssignmentQuantity(rowId: string, weekKey: string, assignmentId: string, quantity: number): Promise<boolean> {
    const allocation = getAllocation(rowId, weekKey);
    if (!allocation) {
      return false;
    }

    try {
      const updated = await updatePoAssignment(allocation.id, assignmentId, quantity);
      allocations.value[rowId][weekKey] = updated;
      logger.showSuccess(quantityUpdateMessage(quantity));
      invalidatePurchaseOrders();
      return true;
    } catch (updateError) {
      logger.showError(extractErrorMessage(updateError, 'Failed to update purchase order assignment'));
      return false;
    }
  }

  // rowId identifies the allocation the removal was initiated from; a
  // distributed PO's other portions live on sibling rows for the same week,
  // resolved via the initiating row's vendorId.
  async function removePoAssignment(rowId: string, weekKey: string, assignmentId: string): Promise<boolean> {
    const allocation = getAllocation(rowId, weekKey);
    if (!allocation) {
      return false;
    }

    const initiatingRow = rows.value.find((row) => row.id === rowId);

    try {
      const results = await deletePoAssignment(allocation.id, assignmentId);
      patchAllocationFromResults(initiatingRow?.vendorId, results);
      invalidatePurchaseOrders();
      return true;
    } catch (removeError) {
      logger.showError(extractErrorMessage(removeError, 'Failed to remove purchase order assignment'));
      return false;
    }
  }

  // Patches every touched allocation rather than just this row's: a distributed block's
  // note is cascaded across its sibling rows, which sit on other capacity types.
  // No invalidatePurchaseOrders — a note changes no quantity, so PO availability is untouched.
  async function updatePoAssignmentNotes(rowId: string, weekKey: string, assignmentId: string, notes: string): Promise<boolean> {
    const allocation = getAllocation(rowId, weekKey);
    if (!allocation) {
      return false;
    }

    const initiatingRow = rows.value.find((row) => row.id === rowId);

    try {
      const results = await updateAssignmentNotes(allocation.id, assignmentId, notes);
      patchAllocationFromResults(initiatingRow?.vendorId, results);
      logger.showSuccess(noteUpdateMessage(notes));
      return true;
    } catch (noteError) {
      logger.showError(extractErrorMessage(noteError, 'Failed to save the note'));
      return false;
    }
  }

  // A null poNumber distributes a manual block: same grouped write, manual-type rows.
  // `quiet` is for a multi-week split: the caller toasts, highlights and invalidates once
  // after the last week instead of once per call.
  async function distributePurchaseOrder(
    vendorId: string,
    weekKey: string,
    poNumber: string | null,
    portions: DistributionPortion[],
    notes?: string,
    productCategory?: string,
    options: { quiet?: boolean } = {},
  ): Promise<boolean> {
    try {
      const results = await requestDistribution({
        vendorId,
        weekStart: weekKey,
        ...(poNumber !== null ? { poNumber } : {}),
        portions,
        ...(notes !== undefined ? { notes } : {}),
        ...(productCategory !== undefined ? { productCategory } : {}),
      });
      patchAllocationFromResults(vendorId, results);
      if (options.quiet) return true;
      if (poNumber) {
        setRecentAssignments([poNumber], [weekKey]);
      }
      logger.showSuccess(distributionSuccessMessage(poNumber, portions.length));
      invalidatePurchaseOrders();
      return true;
    } catch (distributeError) {
      logger.showError(extractErrorMessage(distributeError, 'Failed to distribute purchase order'));
      return false;
    }
  }

  async function moveAssignment(rowId: string, weekKey: string, assignmentId: string, targetWeekStart: string): Promise<boolean> {
    const allocation = getAllocation(rowId, weekKey);
    if (!allocation) {
      return false;
    }

    const initiatingRow = rows.value.find((row) => row.id === rowId);

    try {
      const results = await requestMoveAssignment(allocation.id, assignmentId, targetWeekStart);
      patchAllocationFromResults(initiatingRow?.vendorId, results);
      logger.showSuccess(moveSuccessMessage(targetWeekStart));
      invalidatePurchaseOrders();
      return true;
    } catch (moveError) {
      logger.showError(extractErrorMessage(moveError, 'Failed to move purchase order'));
      return false;
    }
  }

  async function moveDistributionGroup(rowId: string, distributionGroupId: string, targetWeekStart: string): Promise<boolean> {
    const initiatingRow = rows.value.find((row) => row.id === rowId);

    try {
      const results = await requestMoveDistributionGroup(distributionGroupId, targetWeekStart);
      patchAllocationFromResults(initiatingRow?.vendorId, results);
      logger.showSuccess(moveSuccessMessage(targetWeekStart));
      invalidatePurchaseOrders();
      return true;
    } catch (moveError) {
      logger.showError(extractErrorMessage(moveError, 'Failed to move distribution group'));
      return false;
    }
  }

  // Parses a plan file — does not itself write anything. pendingDiff picks up the diff against
  // the calendar reactively (see its definition above). A second call simply replaces whatever
  // preview was pending.
  async function startImportPreview(file: File): Promise<boolean> {
    importParsing.value = true;
    try {
      const parse = await parseCapacityPlan(file);
      pendingImport.value = { fileName: file.name, parse };
      excludedPendingKeys.value = new Set();
      return true;
    } catch (parseError) {
      logger.showError(extractErrorMessage(parseError, 'Failed to parse the import file'));
      return false;
    } finally {
      importParsing.value = false;
    }
  }

  // "Import applied — N weeks updated across M vendors", plus an honest note when the server
  // skipped weeks someone else had already changed since the preview was built (concurrent edit,
  // not a bug) — those weeks were left as-is rather than overwritten.
  function buildImportResultMessage(result: CapacityImportConfirmResult): string {
    const totals = result.vendors.reduce(
      (acc, vendor) => ({
        applied: acc.applied + vendor.allocationsCreated + vendor.allocationsUpdated,
        skipped: acc.skipped + vendor.allocationsSkipped,
      }),
      { applied: 0, skipped: 0 },
    );

    let message = `Import applied — ${totals.applied} week${totals.applied === 1 ? '' : 's'} updated across ${result.vendors.length} vendor${result.vendors.length === 1 ? '' : 's'}`;
    if (totals.skipped > 0) {
      message += ` — ${totals.skipped} week${totals.skipped === 1 ? ' was' : 's were'} already changed by someone else and kept`;
    }
    return message;
  }

  // Writes the pending import exactly like the import page does, then silently refreshes and
  // flashes the cells that were just written. Keeps the preview on failure so the user can
  // retry or discard instead of losing their place.
  //
  // `pending`/`diff` are captured up front — `diff` is a snapshot of the reactive pendingDiff at
  // the moment approve was clicked, so a mutation landing mid-request (e.g. from another tab's
  // refresh) can't rewrite the payload out from under this call. Every later mutation of shared
  // state is guarded on `pendingImport.value === pending` — a confirm request can take long
  // enough for the user to discard this preview (it should just vanish, not resurrect) or, if
  // the toolbar ever allows it again, start a fresh one (whose diff must never be clobbered by
  // this stale request landing late). The guard is checked and acted on BEFORE refresh() runs so
  // refresh's own entries/allocations swap only ever affects a still-active, unrelated preview's
  // (reactive) diff — never resurrects the one this call is in the middle of clearing.
  async function approvePendingImport(): Promise<boolean> {
    const pending = pendingImport.value;
    const diff = pendingDiff.value;
    if (!pending || !diff) return false;
    const { parse } = pending;

    approving.value = true;
    try {
      const result = await confirmCapacityImport({
        // A 'partial' match is an uncertain page-level guess; only an 'exact' match is safe
        // to fall a vendorless line back onto (mirrors capacityImportStore.confirmImport's gate) —
        // otherwise the backend's groupLinesByVendor would silently write to the wrong vendor.
        vendorId: parse.vendorMatchConfidence === 'exact' ? (parse.vendorMatch?.id ?? null) : null,
        importRecordId: parse.importRecordId,
        weeks: parse.weeks,
        // Whitelist semantics (see buildApprovedProductionLines): every week NOT represented in
        // diff.cells — excluded, unchanged, or belonging to a "needs attention" line the preview
        // never rendered — is nulled out. The backend's buildDrafts skips a null capacity value
        // entirely, so this genuinely writes nothing for that vendor/type/week.
        productionLines: buildApprovedProductionLines(parse, diff.cells),
        overwrite: buildOverwriteTargets(diff),
      });

      if (pendingImport.value === pending) {
        pendingImport.value = null;
        excludedPendingKeys.value = new Set();
      }

      await refresh();
      markRecentCells(diff.cells.map((cell) => `${cell.rowId}:${cell.weekStart}`));
      logger.showSuccess(buildImportResultMessage(result));
      return true;
    } catch (confirmError) {
      if (pendingImport.value === pending) {
        logger.showError(extractErrorMessage(confirmError, 'Failed to import the capacity plan'));
      }
      return false;
    } finally {
      approving.value = false;
    }
  }

  // Removes one pending cell from the preview (the × button on a ghost cell) — adding its key to
  // excludedPendingKeys is all that's needed now: pendingDiff (computed) re-applies exclusions on
  // every read, so the cell disappears from pendingDiff.cells immediately, which is what both the
  // grid's pending-overlay lookup and buildApprovedProductionLines' whitelist key off.
  function excludePendingCell(rowId: string, weekStart: string): void {
    if (!pendingImport.value) return;

    const matches = pendingDiff.value?.cells.some((cell) => cell.rowId === rowId && cell.weekStart === weekStart);
    if (!matches) return;

    excludedPendingKeys.value = new Set([...excludedPendingKeys.value, `${rowId}:${weekStart}`]);
  }

  function discardPendingImport(): void {
    pendingImport.value = null;
    excludedPendingKeys.value = new Set();
  }

  return {
    allocations,
    distributionGroups,
    error,
    loading,
    lockedVendorIds,
    recentAssignments,
    recentCells,
    pendingImport,
    pendingDiff,
    importParsing,
    approving,
    excludedCount,
    rows,
    load,
    refresh,
    updateAllocationParameters,
    createAllocation,
    deleteAllocation,
    assignPurchaseOrders,
    updatePoAssignmentQuantity,
    removePoAssignment,
    updatePoAssignmentNotes,
    distributePurchaseOrder,
    moveAssignment,
    moveDistributionGroup,
    markRecentCells,
    setRecentAssignments,
    invalidatePurchaseOrders,
    startImportPreview,
    approvePendingImport,
    excludePendingCell,
    discardPendingImport,
  };
});
